/**
 * Google Analytics 4 - Event Tracking Module
 * Atlantique Spa Boutique
 * 
 * ============================================
 * CONFIGURATION
 * ============================================
 * 
 * Pour activer GA4, ajoutez votre Measurement ID ci-dessous :
 */
const GA4_MEASUREMENT_ID = 'G-MLYLFG72ZV';

// Debug logging only in development
const GA4_DEBUG = process.env.NODE_ENV === 'development';
const debugLog = (...args) => { if (GA4_DEBUG) console.log(...args); };

/**
 * ============================================
 * ÉVÉNEMENTS E-COMMERCE TRACKÉS
 * ============================================
 * 
 * - page_view: Vue de page (automatique)
 * - view_item: Vue produit/fiche
 * - add_to_cart: Ajout au panier
 * - remove_from_cart: Retrait du panier
 * - view_cart: Vue du panier
 * - begin_checkout: Début du checkout
 * - purchase: Achat réussi
 * 
 * ÉVÉNEMENTS CONVERSION
 * - submit_contact_form: Formulaire contact
 * - submit_devis_form: Demande de devis
 * - submit_compatibility_form: Vérification compatibilité
 */

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Check if GA4 is configured
const isGA4Configured = () => {
  return GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID.startsWith('G-');
};

/**
 * ============================================
 * ÉVÉNEMENTS E-COMMERCE GA4
 * ============================================
 */

/**
 * Track product/item view (view_item)
 * @param {Object} item - Product details
 */
export const trackViewItem = (item) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] view_item:', item);
    return;
  }
  
  window.gtag('event', 'view_item', {
    currency: 'EUR',
    value: item.price || 0,
    items: [{
      item_id: item.id || item.slug,
      item_name: item.name,
      item_category: item.category || 'Pièces détachées',
      item_brand: item.brand || 'Compatible Spa',
      price: item.price || 0,
      quantity: 1
    }]
  });
};

/**
 * Track add to cart (add_to_cart)
 * @param {Object} item - Product details
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (item, quantity = 1) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] add_to_cart:', { item, quantity });
    return;
  }
  
  window.gtag('event', 'add_to_cart', {
    currency: 'EUR',
    value: (item.price || 0) * quantity,
    items: [{
      item_id: item.id || item.slug,
      item_name: item.name,
      item_category: item.category || 'Pièces détachées',
      item_brand: item.brand || 'Compatible Spa',
      price: item.price || 0,
      quantity: quantity
    }]
  });
};

/**
 * Track remove from cart (remove_from_cart)
 * @param {Object} item - Product details
 * @param {number} quantity - Quantity removed
 */
export const trackRemoveFromCart = (item, quantity = 1) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] remove_from_cart:', { item, quantity });
    return;
  }
  
  window.gtag('event', 'remove_from_cart', {
    currency: 'EUR',
    value: (item.price || 0) * quantity,
    items: [{
      item_id: item.id || item.slug,
      item_name: item.name,
      item_category: item.category || 'Pièces détachées',
      price: item.price || 0,
      quantity: quantity
    }]
  });
};

/**
 * Track view cart (view_cart)
 * @param {Array} items - Cart items
 * @param {number} total - Cart total
 */
export const trackViewCart = (items, total) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] view_cart:', { items, total });
    return;
  }
  
  const ga4Items = items.map(item => ({
    item_id: item.id || item.slug || item.product_id,
    item_name: item.name,
    item_category: item.category || item.product_type || 'Pièces détachées',
    price: item.price || 0,
    quantity: item.quantity || 1
  }));
  
  window.gtag('event', 'view_cart', {
    currency: 'EUR',
    value: total,
    items: ga4Items
  });
};

/**
 * Track begin checkout (begin_checkout)
 * @param {Array} items - Cart items
 * @param {number} total - Cart total
 * @param {number} shipping - Shipping cost
 */
export const trackBeginCheckout = (items, total, shipping = 0) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] begin_checkout:', { items, total, shipping });
    return;
  }
  
  const ga4Items = items.map(item => ({
    item_id: item.id || item.slug || item.product_id,
    item_name: item.name,
    item_category: item.category || item.product_type || 'Pièces détachées',
    price: item.price || 0,
    quantity: item.quantity || 1
  }));
  
  window.gtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: total,
    items: ga4Items,
    shipping: shipping
  });
};

/**
 * Track purchase success (purchase)
 * @param {Object} order - Order details
 */
export const trackPurchase = (order) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] purchase:', order);
    return;
  }
  
  const ga4Items = (order.items || []).map(item => ({
    item_id: item.id || item.slug || item.product_id,
    item_name: item.name,
    item_category: item.category || item.product_type || 'Pièces détachées',
    price: item.price || 0,
    quantity: item.quantity || 1
  }));
  
  window.gtag('event', 'purchase', {
    transaction_id: order.order_number || order.transaction_id,
    value: order.total || order.total_amount || 0,
    tax: 0, // TVA incluse dans les prix TTC
    shipping: order.shipping_cost || order.shipping_cost_ttc || 0,
    currency: 'EUR',
    items: ga4Items
  });
};

/**
 * ============================================
 * ÉVÉNEMENTS CONVERSION
 * ============================================
 */

/**
 * Track contact form submission
 */
export const trackContactFormSubmit = () => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] submit_contact_form');
    return;
  }
  
  window.gtag('event', 'generate_lead', {
    event_category: 'conversion',
    event_label: 'contact_form'
  });
};

/**
 * Track devis (quote) form submission
 * @param {Object} params - Event parameters
 */
export const trackDevisFormSubmit = ({ product_slug = '', product_name = '' }) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] submit_devis_form:', { product_slug, product_name });
    return;
  }
  
  window.gtag('event', 'generate_lead', {
    event_category: 'conversion',
    event_label: 'devis_form',
    product_slug,
    product_name
  });
};

/**
 * Track compatibility form submission
 * @param {Object} params - Event parameters
 */
export const trackCompatibilityFormSubmit = ({ product_slug, category = '' }) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] submit_compatibility_form:', { product_slug, category });
    return;
  }
  
  window.gtag('event', 'generate_lead', {
    event_category: 'conversion',
    event_label: 'compatibility_form',
    product_slug,
    category
  });
};

/**
 * ============================================
 * ÉVÉNEMENTS ENGAGEMENT
 * ============================================
 */

/**
 * Track product page view (legacy - use trackViewItem for e-commerce)
 */
export const trackProductView = ({ product_slug, category = '', product_name = '' }) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] view_product:', { product_slug, category, product_name });
    return;
  }
  
  window.gtag('event', 'view_item', {
    currency: 'EUR',
    items: [{
      item_id: product_slug,
      item_name: product_name,
      item_category: category
    }]
  });
};

/**
 * Track spa page view
 */
export const trackSpaView = ({ spa_slug, spa_name = '', gamme = '' }) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] view_spa:', { spa_slug, spa_name, gamme });
    return;
  }
  
  window.gtag('event', 'view_item', {
    currency: 'EUR',
    items: [{
      item_id: spa_slug,
      item_name: spa_name,
      item_category: 'Spas',
      item_variant: gamme
    }]
  });
};

/**
 * Track local SEO page view
 */
export const trackLocalPageView = (location) => {
  if (!isGtagAvailable()) {
    debugLog('[GA4 Debug] view_local_page:', { location });
    return;
  }
  
  window.gtag('event', 'page_view', {
    page_title: `Spa ${location}`,
    page_location: window.location.href
  });
};

/**
 * Legacy phone click tracking (deprecated - no phone on site)
 */
export const trackPhoneClick = () => {
  debugLog('[GA4] Phone tracking deprecated - email only');
};

/**
 * Legacy WhatsApp click tracking (deprecated)
 */
export const trackWhatsAppClick = () => {
  debugLog('[GA4] WhatsApp tracking deprecated');
};

/**
 * ============================================
 * INITIALISATION GA4
 * ============================================
 */

/**
 * Initialize GA4 - call this in App.js or index.js
 */
export const initGA4 = () => {
  if (!isGA4Configured()) {
    debugLog('[GA4] Not configured - add your Measurement ID in analytics.js');
    return false;
  }
  
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure'
  });
  
  debugLog('[GA4] Initialized with ID:', GA4_MEASUREMENT_ID);
  return true;
};

// Export configuration status
export const getGA4Status = () => ({
  configured: isGA4Configured(),
  measurementId: GA4_MEASUREMENT_ID || 'Non configuré',
  gtagAvailable: isGtagAvailable()
});

// Export all tracking functions
export default {
  // E-commerce events
  trackViewItem,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackPurchase,
  // Conversion events
  trackContactFormSubmit,
  trackDevisFormSubmit,
  trackCompatibilityFormSubmit,
  // Engagement events
  trackProductView,
  trackSpaView,
  trackLocalPageView,
  // Initialization
  initGA4,
  getGA4Status
};
